import { ExecutiveReportSectionInterface } from './report-section/interface';

/**
 * ExecutiveReportSection
 * A enum containing each possible section that can appear in the list. This will need to be
 * kept in-sync with AvailableExecutiveReportSections
 */
export const ExecutiveReportSection = {
  CUSTOMER_RELATIONS: 'CUSTOMER_RELATIONS',
  OVERVIEW: 'OVERVIEW',
  GOOGLE_MY_BUSINESS: 'GOOGLE_MY_BUSINESS',
  LISTINGS: 'LISTINGS',
  REPUTATION: 'REPUTATION',
  SOCIAL: 'SOCIAL',
  ADVERTISING: 'ADVERTISING',
  WEBSITE: 'WEBSITE',
  FILES: 'FILES',
  SERVICES: 'SERVICES',
  SEO: 'SEO',
  ACCOUNTING: 'ACCOUNTING',
  BING_INSIGHTS: 'BING_INSIGHTS',
} as const;

export type ExecutiveReportSection = (typeof ExecutiveReportSection)[keyof typeof ExecutiveReportSection];

/**
 * AvailableExecutiveReportSections
 * A list of available sections that can be in the executive report. If you are adding a new section,
 * please add it to the ExecutiveReportSection enum above.
 */
export const AvailableExecutiveReportSections: Record<ExecutiveReportSection, ExecutiveReportSectionInterface> = {
  OVERVIEW: {
    uniqueId: 'overview',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.OVERVIEW',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.OVERVIEW',
    iconName: 'timeline',
    hideTitle: true,
  },
  FILES: {
    uniqueId: 'files',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.FILES',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.FILES',
    sectionDescriptionKey: '',
    iconName: 'description',
    hideTitle: true,
  },
  CUSTOMER_RELATIONS: {
    uniqueId: 'customer_relations',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.LEADS',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.LEADS',
    sectionDescriptionKey: 'EXECUTIVE_REPORT.CATEGORY_QUESTION.LEADS',
    iconName: 'people',
  },
  GOOGLE_MY_BUSINESS: {
    uniqueId: 'gmb',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.GOOGLE_MY_BUSINESS',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.GOOGLE_MY_BUSINESS',
    iconUrl: 'google-logo-2.svg',
  },
  SEO: {
    uniqueId: 'seo',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.SEO',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.SEO',
    sectionDescriptionKey: 'EXECUTIVE_REPORT.CATEGORY_QUESTION.SEO',
    iconName: 'search',
  },
  LISTINGS: {
    uniqueId: 'listings',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.LISTINGS',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.LISTINGS',
    sectionDescriptionKey: 'EXECUTIVE_REPORT.CATEGORY_QUESTION.LISTINGS',
    iconName: 'place',
  },
  BING_INSIGHTS: {
    uniqueId: 'bing_insights',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.BING_INSIGHTS',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.BING_INSIGHTS',
    iconUrl: 'bing-logo.svg',
  },
  ADVERTISING: {
    uniqueId: 'advertising',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.ADVERTISING',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.ADVERTISING',
    sectionDescriptionKey: 'EXECUTIVE_REPORT.CATEGORY_QUESTION.ADVERTISING',
    iconName: 'public',
  },
  WEBSITE: {
    uniqueId: 'website',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.WEBSITE',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.WEBSITE',
    sectionDescriptionKey: 'EXECUTIVE_REPORT.CATEGORY_QUESTION.WEBSITE',
    iconName: 'web',
  },
  REPUTATION: {
    uniqueId: 'reputation',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.REPUTATION',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.REPUTATION',
    sectionDescriptionKey: 'EXECUTIVE_REPORT.CATEGORY_QUESTION.REPUTATION',
    iconName: 'star',
  },
  SOCIAL: {
    uniqueId: 'social',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.SOCIAL',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.SOCIAL',
    sectionDescriptionKey: 'EXECUTIVE_REPORT.CATEGORY_QUESTION.SOCIAL',
    iconName: 'chat_bubble',
  },
  SERVICES: {
    uniqueId: 'services',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.SERVICES',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.SERVICES',
    sectionDescriptionKey: 'EXECUTIVE_REPORT.CATEGORY_QUESTION.SERVICES',
    iconName: 'check_circle',
  },
  ACCOUNTING: {
    uniqueId: 'accounting',
    sectionId: 'EXECUTIVE_REPORT.CATEGORY_TITLE.ACCOUNTING',
    sectionTitleKey: 'EXECUTIVE_REPORT.CATEGORY_TITLE.ACCOUNTING',
    iconName: 'local_atm',
  },
};
